import React from 'react';
import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import { BRAND_FULL_NAME, BRAND_NAME, logoImage } from '../../config/brand';

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  alternateLink?: { prompt: string; label: string; to: string };
  footerNote?: string;
  wide?: boolean;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
  alternateLink,
  footerNote,
  wide = false,
}) => (
  <div className="relative min-h-screen overflow-hidden bg-[#F4F6FA] font-cairo" dir="rtl">
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
    >
      <div className="absolute -top-24 -start-16 h-72 w-72 rounded-full bg-[#001848]/5 blur-3xl" />
      <div className="absolute top-1/3 -end-20 h-80 w-80 rounded-full bg-gold-400/15 blur-3xl" />
      <div className="absolute -bottom-28 start-1/3 h-64 w-64 rounded-full bg-[#001848]/[0.04] blur-3xl" />
    </div>

    <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col items-center justify-center px-4 py-8 sm:px-6 sm:py-10">
      <Link
        to="/"
        className="mb-5 inline-flex min-h-11 items-center gap-1.5 self-start rounded-xl px-2 text-sm font-medium text-[#001848]/70 transition-colors hover:bg-white/70 hover:text-[#001848] sm:mb-6"
      >
        <Home className="h-4 w-4" />
        العودة للرئيسية
      </Link>

      <div className={`w-full ${wide ? 'max-w-xl' : 'max-w-md'}`}>
        <div className="overflow-hidden rounded-2xl border border-white/80 bg-white shadow-[0_12px_40px_-12px_rgba(0,24,72,0.18)] sm:rounded-3xl">
          <div className="h-1.5 w-full bg-gradient-to-l from-gold-400 via-gold-500 to-[#001848]" />

          <div className="p-5 sm:p-8 lg:p-9">
            <div className="mb-6 text-center sm:mb-8">
              <img
                src={logoImage}
                alt={BRAND_NAME}
                className="mx-auto mb-3 h-16 w-auto max-w-[9rem] object-contain sm:h-20 sm:max-w-[11rem]"
              />
              <p className="mb-4 text-xs font-semibold tracking-wide text-slate-500 sm:text-sm">
                {BRAND_FULL_NAME}
              </p>
              <h1 className="mb-2 text-2xl font-bold leading-tight text-[#001848] font-cairo-bold sm:text-3xl">
                {title}
              </h1>
              <p className="mx-auto max-w-sm text-sm leading-relaxed text-slate-500 sm:text-base">
                {subtitle}
              </p>
            </div>

            {children}

            {alternateLink && (
              <p className="mt-7 border-t border-slate-100 pt-6 text-center text-sm text-slate-500">
                {alternateLink.prompt}{' '}
                <Link
                  to={alternateLink.to}
                  className="font-bold text-[#001848] underline decoration-gold-400 underline-offset-4 transition-colors hover:text-gold-600"
                >
                  {alternateLink.label}
                </Link>
              </p>
            )}
          </div>
        </div>

        {footerNote && (
          <p className="mt-5 text-center text-xs leading-relaxed text-slate-400">{footerNote}</p>
        )}
      </div>
    </div>
  </div>
);

export default AuthLayout;
